import { kAgentLegalCitationLinkHrefRegex, kCitationMarkerRegex, replaceCitations } from '@/util/agent-citations'
import { kUnverifiedSourceLabel } from '@/agent/references/constants'
import { AgentReferenceType, ClientAgentReference } from '@/agent/references/store/schemas'
import { AgentReferencesRecord } from '@/agent/references/store/slice'
import { FootnoteInfoByCitationId } from '@/agent/references/util/agent-reference-footnotes'
import { agentReferenceMetadataToTitle } from '@/agent/references/util/agent-reference-title'
import { buildCaselawStandaloneViewerURL } from '@/standalone-reference-viewer/caselaw/utils/build-caselaw-viewer-url'
import { FileReferenceTargetKind, isFileBackedReference, resolveFileReferenceTarget } from '@/agent/references/util/file-reference-target'

export type ReferenceInfo = {
  citationId: string
  label: string
  viewerUrl: string | null
  displayName: string
}

export type ReferenceInfoByCitationId = Record<string, ReferenceInfo>

export type BuildReferenceInfoOptions = {
  includeLegacyFileViewerUrls?: boolean
}

/**
 * Build a viewer URL for a reference based on its type.
 */
function resolveViewerUrl(reference: ClientAgentReference, matterId: string | null, options: BuildReferenceInfoOptions): string | null {
  if (isFileBackedReference(reference)) {
    const target = resolveFileReferenceTarget({
      reference,
      matterId,
    })
    if (target.kind === FileReferenceTargetKind.CANONICAL) return target.viewerUrl
    if (target.kind === FileReferenceTargetKind.LEGACY) {
      if (options.includeLegacyFileViewerUrls === false) return null
      return target.legacyViewerUrl
    }
    return null
  }

  switch (reference.type) {
    case AgentReferenceType.LRR: {
      const s3Path = reference.metadata.s3_path
      if (!s3Path) return null
      return `/dashboard/reference-viewer/lrr/${encodeURIComponent(s3Path)}`
    }
    case AgentReferenceType.CASELAW:
      // Double cast required: buildCaselawStandaloneViewerURL accepts Record<string, unknown>
      // because it checks for s3_dir/s3_path keys dynamically via CaselawStorageField enum
      return buildCaselawStandaloneViewerURL(reference.metadata as unknown as Record<string, unknown>) || null
    default:
      return null
  }
}

/**
 * Build a ReferenceInfoByCitationId from the per-turn footnote info map and the Redux references record.
 * Footnote numbers are sourced from `useFootnoteInfoByCitationId` (or its underlying util) so
 * numbering matches the chat bubbles for the same turn.
 *
 * @param footnoteInfoByCitationId - per-citation footnote rendering data for the turn
 * @param references - the Redux references record keyed by citation_id
 * @param matterId - the conversation's matter ID, used to build file upload viewer URLs
 */
export function buildReferenceInfoByCitationId(
  footnoteInfoByCitationId: FootnoteInfoByCitationId,
  references: AgentReferencesRecord,
  matterId: string | null = null,
  options: BuildReferenceInfoOptions = {}
): ReferenceInfoByCitationId {
  const referenceInfoByCitationId: ReferenceInfoByCitationId = {}
  for (const [citationId, info] of Object.entries(footnoteInfoByCitationId)) {
    const ref = references[citationId]
    if (!ref) continue
    referenceInfoByCitationId[citationId] = {
      citationId,
      label: String(info.footnoteNumber),
      viewerUrl: resolveViewerUrl(ref, matterId, options),
      // Same util as the chat tooltip, so the two surfaces can never disagree.
      displayName: agentReferenceMetadataToTitle(ref),
    }
  }
  return referenceInfoByCitationId
}

/**
 * The canvas span-attribute contract: emitted by the builders below, parsed by the
 * ProseMirror schema's parseDOM, and matched again by the turndown rules. Those files
 * import these constants — a restated literal with a typo would silently strip
 * citations on the parse path.
 */
export const kLegalCitationIdAttr = 'data-legal-citation-id'
export const kLegalCitationUrlAttr = 'data-legal-citation-url'
export const kLegalCitationDisplayNameAttr = 'data-legal-citation-display-name'
export const kLegalCitationNotLinkedAttr = 'data-legal-citation-not-linked'
export const kReferenceMarkerIdAttr = 'data-citation-id'
export const kReferenceMarkerLabelAttr = 'data-citation-label'
export const kReferenceMarkerUrlAttr = 'data-citation-url'
export const kReferenceMarkerDisplayNameAttr = 'data-citation-display-name'

/**
 * HTML-encode a string for use in an HTML attribute value.
 */
function encodeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** The <span> that ProseMirror parses into a legalCitation mark on the authority text. */
function legalCitationSpan(info: ReferenceInfo, authorityText: string, notLinked = false): string {
  const notLinkedAttr = notLinked ? ` ${kLegalCitationNotLinkedAttr}="true"` : ''
  return `<span ${kLegalCitationIdAttr}="${encodeAttr(info.citationId)}"${notLinkedAttr} ${kLegalCitationUrlAttr}="${encodeAttr(
    info.viewerUrl ?? ''
  )}" ${kLegalCitationDisplayNameAttr}="${encodeAttr(info.displayName)}">${authorityText}</span>`
}

/**
 * Fallback numbering for citation ids the version can't resolve (e.g. carried across
 * artifact revisions): greyed-out numbers continuing after the known ones, in first
 * appearance order — same id, same number. Returns a lookup that mints on demand.
 */
function notLinkedInfoFactory(referenceInfoByCitationId: ReferenceInfoByCitationId) {
  const knownNumbers = Object.values(referenceInfoByCitationId).map((info) => Number.parseInt(info.label, 10))
  let nextNumber = Math.max(0, ...knownNumbers.filter(Number.isFinite))
  const infoByCitationId: Record<string, ReferenceInfo> = {}
  return (citationId: string): ReferenceInfo => {
    infoByCitationId[citationId] ??= {
      citationId,
      label: String(++nextNumber),
      viewerUrl: null,
      displayName: kUnverifiedSourceLabel,
    }
    return infoByCitationId[citationId]
  }
}

/** The <span> that ProseMirror parses into a referenceMarker badge node. */
function referenceMarkerSpan(info: ReferenceInfo): string {
  return `<span ${kReferenceMarkerIdAttr}="${encodeAttr(info.citationId)}" ${kReferenceMarkerLabelAttr}="${encodeAttr(
    info.label
  )}" ${kReferenceMarkerUrlAttr}="${encodeAttr(info.viewerUrl ?? '')}" ${kReferenceMarkerDisplayNameAttr}="${encodeAttr(info.displayName)}">[${encodeAttr(
    info.citationId
  )}]</span>`
}

/**
 * Replace both citation forms in raw markdown with the <span> elements above, in one
 * pass over one alternation — emitted span markup is never re-scanned. The authority
 * text stays raw markdown so marked() parses the italics inside.
 *
 * Must be called BEFORE marked().
 */
export function replaceCitationsWithSpans(markdown: string, referenceInfoByCitationId: ReferenceInfoByCitationId): string {
  // Unknown ids (references don't persist across artifact versions, e.g. citations
  // carried through a revision) degrade to visibly distinct "not linked" citations:
  // markers as greyed-out numbers, links as greyed-out authority text — and the spans
  // keep the citation id, so the tokens round-trip through saves intact.
  const notLinkedInfo = notLinkedInfoFactory(referenceInfoByCitationId)
  return replaceCitations(markdown, (_token, { citationId, authorityText }) => {
    const info = referenceInfoByCitationId[citationId]
    if (authorityText !== null) {
      // A link never displays a label, so an unknown link must not consume a fallback
      // number — markers alone mint numbers, keeping every surface's numbering aligned.
      return info
        ? legalCitationSpan(info, authorityText)
        : legalCitationSpan({ citationId, label: '', viewerUrl: null, displayName: kUnverifiedSourceLabel }, authorityText, true)
    }
    return referenceMarkerSpan(info ?? notLinkedInfo(citationId))
  })
}

/**
 * Format both citation forms as display text for non-ProseMirror contexts (e.g. the
 * diff review view) where raw markdown is rendered directly: legal citation links
 * become their bare authority text (the citation is self-identifying, and matches
 * what the editor renders minus interactivity); markers become their "[label]".
 */
export function formatCitationsForDisplay(markdown: string, referenceInfoByCitationId: ReferenceInfoByCitationId): string {
  const notLinkedInfo = notLinkedInfoFactory(referenceInfoByCitationId)
  return replaceCitations(markdown, (_token, { citationId, authorityText }) => {
    if (authorityText !== null) return authorityText

    // Unknown id: same fallback number the editor shows for a not-linked citation.
    const info = referenceInfoByCitationId[citationId] ?? notLinkedInfo(citationId)
    return `[${info.label}]`
  })
}

/**
 * True when a generated reference URL points to a Paxton reference viewer route.
 *
 * This is intentionally narrower than "same-origin URL": read-only artifact rendering injects
 * links after sanitization, so only generated citation links should survive as anchors.
 */
export function isReferenceViewerHref(href: string): boolean {
  return href.startsWith('/dashboard/reference-viewer/') || /^\/dashboard\/m\/[^/]+\/reference-viewer\//.test(href)
}

/** Grey "not linked" element for the read-only view, matching the editor canvas. */
function createNotLinkedNode(ownerDocument: Document, text: string | null): HTMLSpanElement {
  const notLinked = ownerDocument.createElement('span')
  notLinked.setAttribute('class', 'text-brand-neutral-400 underline decoration-brand-neutral-400/40 underline-offset-2')
  notLinked.setAttribute('title', kUnverifiedSourceLabel)
  if (text !== null) notLinked.textContent = text
  return notLinked
}

function createReferenceLinkNode(ownerDocument: Document, info: ReferenceInfo): Node {
  if (!info.viewerUrl || !isReferenceViewerHref(info.viewerUrl)) {
    return ownerDocument.createTextNode(`[${info.label}]`)
  }

  const link = ownerDocument.createElement('a')
  link.setAttribute('href', info.viewerUrl)
  link.setAttribute('target', '_blank')
  link.setAttribute('rel', 'noopener noreferrer')
  link.setAttribute('title', info.displayName)
  link.setAttribute('aria-label', `Reference ${info.label}: ${info.displayName}`)
  link.setAttribute('class', 'reference-marker-badge text-brand-700 hover:text-brand-800')
  link.textContent = `[${info.label}]`
  return link
}

/**
 * Rewrite both citation forms in sanitized HTML into reader-facing links.
 *
 * [ref_xxxxxx] markers in text nodes become viewer links; markers without a safe viewer
 * URL fall back to their display label, matching the editor's disabled badge behavior,
 * and markers absent from the map render their not-linked fallback number. Legal citation links arrive as anchors
 * whose href is the bare citation id (markdown parsing of "[authority](ref_x)"): known
 * ids are rewritten to their viewer URL, everything else unwraps to plain authority
 * text — the canvas policy.
 *
 * Must be called AFTER sanitization so user-authored links and HTML attributes are already gone.
 */
export function replaceReferenceMarkersWithLinks(html: string, referenceInfoByCitationId: ReferenceInfoByCitationId): string {
  if (typeof document === 'undefined') return formatCitationsForDisplay(html, referenceInfoByCitationId)

  const regex = new RegExp(kCitationMarkerRegex.source, 'g')
  const notLinkedInfo = notLinkedInfoFactory(referenceInfoByCitationId)
  const template = document.createElement('template')
  template.innerHTML = html

  for (const anchor of template.content.querySelectorAll('a')) {
    const href = anchor.getAttribute('href') ?? ''
    if (!kAgentLegalCitationLinkHrefRegex.test(href)) continue
    const info = referenceInfoByCitationId[href]
    if (info?.viewerUrl && isReferenceViewerHref(info.viewerUrl)) {
      anchor.setAttribute('href', info.viewerUrl)
      anchor.setAttribute('target', '_blank')
      anchor.setAttribute('rel', 'noopener noreferrer')
      anchor.setAttribute('title', info.displayName)
      anchor.setAttribute('class', 'legal-citation-link text-brand-700 hover:text-brand-800')
    } else if (info) {
      // Known reference without a standalone viewer URL: plain authority text.
      anchor.replaceWith(...anchor.childNodes)
    } else {
      // Unknown id: the not-linked treatment, matching the editor canvas.
      const notLinked = createNotLinkedNode(document, null)
      notLinked.append(...anchor.childNodes)
      anchor.replaceWith(notLinked)
    }
  }

  const textNodes: Text[] = []
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text)
  }

  for (const textNode of textNodes) {
    const text = textNode.nodeValue ?? ''
    const matches = [...text.matchAll(regex)]
    if (matches.length === 0) continue

    const fragment = document.createDocumentFragment()
    let lastIndex = 0

    for (const match of matches) {
      const index = match.index ?? 0
      if (index > lastIndex) fragment.append(document.createTextNode(text.slice(lastIndex, index)))

      const citationId = match[0].slice(1, -1)
      const info = referenceInfoByCitationId[citationId]
      if (info) {
        fragment.append(createReferenceLinkNode(document, info))
      } else {
        fragment.append(createNotLinkedNode(document, `[${notLinkedInfo(citationId).label}]`))
      }

      lastIndex = index + match[0].length
    }

    if (lastIndex < text.length) fragment.append(document.createTextNode(text.slice(lastIndex)))
    textNode.replaceWith(fragment)
  }

  return template.innerHTML
}
